/***********************************************************
 TableMixin
 --------------------------
 Mixin to define the props for table components.
 ************************************************************/
import {formatDimension} from "@spoferan/spoferan-ts-core";

export default {

    props: {

        // The accent color to show on specific elements of the table
        color: {
            type: String,
            default: null
        },

        // An array of objects that each describe a header column. See the example below for a definition of all properties
        // {
        //     text: string
        //     abbreviation: string
        //     formatter: function({item, parent, expanded})
        //     value: string
        //     align?: 'start' | 'center' | 'end'
        //     sortable?: boolean
        //     filterable?: boolean
        //     divider?: boolean
        //     class?: string | string[]
        //     width?: string | number
        //     filter?: (value: any, search: string, item: any) => boolean
        //     sort?: (a: any, b: any) => number
        //     nowrap?: boolean
        // }
        headers: {
            type: Array,
            required: true
        },

        // The text to show when no data is provided
        noDataText: {
            type: String,
            default: null
        },

        // The text to show when no data is provided after filtering
        noResultsText: {
            type: String,
            default: null
        },

        // The text to display on a button when no data exists to navigate the user to a corresponding creation page
        // or true to display the button with a default 'add' text
        missingButton: {
            type: [String ,  Boolean],
            default: null
        },

        // The route to navigate the user to when he clicks on the missing button
        missingTo: {
            type: [String ,  Object],
            default: null
        },

        // Whether to fix the header row
        fixedHeader: {
            type: Boolean,
            default: false
        },

        // Whether the table itself shall be displayed in full width whereby additional spacing is applied to wrapping elements
        fullWidth: {
            type: Boolean,
            default: false
        },

        // Whether the table shall be displayed without horizontal scrolling and rather show the additional data on row click
        // Only the first headers (number depending on screen size) will be initially displayed on a table row on mobile devices
        expandOnMobile: {
            type: Boolean,
            default: false
        },

        // Whether to disable the sorting features
        disableSort: {
            type: Boolean,
            default: false
        },

        // Whether to append an 'action' dropdown at the end of each row
        withActions: {
            type: Boolean,
            default: false
        },

        // The prop name of the table items that hold sub rows to display on expand
        subRowsProp: {
            type: String,
            default: null
        },

        // The prop name of the table items that states whether a row might have sub rows
        // Useful when the subRowsProp might not exist yet on a row due to lazy loading
        subRowsExistenceProp: {
            type: String,
            default: null
        },

        // The prop name of the table items that states how many sub rows a row has
        // Useful to show a loader when loading the sub rows data
        subRowsCountProp: {
            type: String,
            default: null
        },

        // The prop name of the table items that hold a boolean whether an item row shall be displayed as disabled
        disabledProp: {
            type: String,
            default: null
        },

        // The max height of the table
        maxHeight: {
            type: [Number ,  String],
            default: null
        },

        // A set of groups for grouping the table using the 'groupProp' property name
        // The following data of a group is used:
        // - id: Must match the value of the groupProp property of a table item to place in the group
        // - title: A title to display for the group
        // - description: A description to display for the group
        groups: {
            type: Array,
            default: null
        },

        // A prop of the table items to group the items; required 'groups' to be set
        groupProp: {
            type: String,
            default: null
        }
    },

    computed: {
        displayHeaders() {
            let headers = [...this.headers];

            // We only display the first headers whose width are within the max width, if a reduced view shall be displayed
            if (this.displayWithMobileExpand) {
                const maxWidthFactor = this.$breakpoint.xsOnly ? 8 : 12;
                const mobileHeaders = [];
                let widthFactorSum = 0;
                headers.forEach(header => {
                    widthFactorSum += (header.width_factor ?? (header.width ? Math.round(header.width / 50) : 1));
                    if (widthFactorSum <= maxWidthFactor) {
                        mobileHeaders.push(header);
                    }
                })
                headers = mobileHeaders;
            }

            const withActionsHeader = this.withActions && headers.length && !headers.find(header => header.value === 'actions');

            if ((this.subRowsProp || this.subRowsExistenceProp || this.displayWithMobileExpand) && (!headers.length || headers[0].value !== 'expand')) {
                if (withActionsHeader) {
                    headers.splice(0,0,{value: 'expand', sortable: false, filterable: false})
                } else {
                    headers.push({value: 'expand', sortable: false, filterable: false})
                }
            }

            if (withActionsHeader) {
                headers.push({value: 'actions', sortable: false, filterable: false, align: 'center', width: 44, sticky: 'right'})
            }

            return headers;
        },

        displayWithMobileExpand() {
            return this.expandOnMobile && this.$breakpoint.smAndDown;
        },

        tableMaxHeightStyle() {
            if (this.maxHeight) {
                return formatDimension(this.maxHeight);
            }

            return this.fixedHeader && this.$breakpoint.mdAndUp ? '80vh' : null;
        }
    },

    methods: {

        // Checks whether the given row has sub rows that can be toggled
        hasSubRows(row) {
            return !!((this.subRowsProp && row[this.subRowsProp]) || (this.subRowsExistenceProp && row[this.subRowsExistenceProp]));
        },

        // Checks whether the given row can be toggle to display more information
        canBeExpanded(row) {
            return this.displayWithMobileExpand || this.hasSubRows(row);
        },

        getItemGroup(item) {
            if (!this.groupProp || !this.groups || !this.groups.length) {
                return null;
            }

            return this.groups.find(group => group.id === item[this.groupProp]);
        }
    }
}

